import type { TScheduleRootStatus } from '@lucimark/shared/models/player'
import type {
  IChannelPayloadPlaylistAssignment,
  IPlayerRegistrationPayload,
  IScheduleRootAssignment
} from '@lucimark/shared/models/player-registration-payload'
import type { TScheduleRootPlaylistAssignment } from '@lucimark/shared/models/schedule-root-types'
import type { TRegistrationStatusPlayerGroupDisplay } from '@/helpers/registrationPlayerGroupDisplay'
import { Temporal } from '@js-temporal/polyfill'
import { PLAYER_POOLING_PERIOD_MS } from '@lucimark/shared/constants/player'
import { CampaignStatus } from '@lucimark/shared/database/custom-types/campaign-status'
import { aspectRatioFromPlaylistFrameLayouts } from '@lucimark/shared/helpers/aspectRatioFromPlaylistFrameLayouts'
import { combineRegistrationScheduleRootsForPlayback } from '@lucimark/shared/helpers/combineRegistrationScheduleRootsForPlayback'
import { operatorAssignedPlayerMasterPlaylistUrn } from '@lucimark/shared/helpers/playerMasterPlaylist'
import { parseRegistrationStatusOverlayFields } from '@lucimark/shared/models/player-overlay-ui'
import { getPlayerRegistrationSchedule } from '@lucimark/shared/models/player-registration-payload'
import { parsePlayerWeeklyAvailabilitySchedule } from '@lucimark/shared/models/player-weekly-availability'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { playerGroupDisplayFromRegistrationPayload } from '@/helpers/registrationPlayerGroupDisplay'

/**
 * Raw registration-status API response (dates as ISO strings).
 * Contract: API must always send `webOta` (object or null) for registered players so the client can apply/rollback web OTA.
 * If the key is missing, the adapter leaves webOta undefined; the player uses the last explicit registration-status `webOta`, then the WS handshake offer.
 */
export type RegistrationStatusApiRaw = IPlayerRegistrationPayload & {
  serverTimeMs?: number
  /** @deprecated Ignored; retained so old fixtures deserialize. */
  playerGroup?: unknown
}

/** Parses registration-status API response into IPlayerRegistrationPayload. */
export function parseRegistrationStatus(raw: RegistrationStatusApiRaw | null): IPlayerRegistrationPayload | null {
  if (raw === null) {
    return null
  }
  const { serverTimeMs, playerGroup: _legacyPlayerGroup, schedule: _legacySchedule, ...rest } = raw
  const playlistsNormalized = Array.isArray(raw.playlists) ? raw.playlists : undefined
  const overlay = parseRegistrationStatusOverlayFields({
    remoteOverlayUi: raw.remoteOverlayUi as unknown,
    overlayUiDirectiveSeq: raw.overlayUiDirectiveSeq as unknown
  })
  return {
    ...rest,
    availabilityWeeklySchedule: parsePlayerWeeklyAvailabilitySchedule(
      'availabilityWeeklySchedule' in raw ? raw.availabilityWeeklySchedule : null
    ),
    distributionLists: raw.distributionLists ?? [],
    ...(playlistsNormalized !== undefined ? { playlists: playlistsNormalized } : {}),
    ...(serverTimeMs != null && Number.isFinite(serverTimeMs) ? { serverTimeMs } : {}),
    ...('webOta' in raw ? { webOta: raw.webOta ?? null } : {}),
    ...(overlay != null ? overlay : {}),
    ...(raw.assetCacheRepairDirectiveSeq != null
      && Number.isInteger(raw.assetCacheRepairDirectiveSeq)
      && raw.assetCacheRepairDirectiveSeq > 0
      ? { assetCacheRepairDirectiveSeq: raw.assetCacheRepairDirectiveSeq }
      : {}),
    ...('audienceAnalyticsEnabled' in raw && typeof raw.audienceAnalyticsEnabled === 'boolean'
      ? { audienceAnalyticsEnabled: raw.audienceAnalyticsEnabled }
      : {}),
    ...('audienceVideoInputDeviceId' in raw
      ? { audienceVideoInputDeviceId: raw.audienceVideoInputDeviceId ?? null }
      : {})
  }
}

/**
 * Adapts IPlayerRegistrationPayload from the registration-status API
 * to TPlayerRegistrationStatus shape expected by the player UI and playback materialization.
 *
 * Multiplexes all active schedule roots (master first, then distribution-list order) for playback.
 */
export function adaptRegistrationPayload(
  payload: IPlayerRegistrationPayload | null
): TPlayerRegistrationStatusAdapter | null {
  if (payload === null) {
    return null
  }
  const { tenant } = payload
  const campaigns = getPlayerRegistrationSchedule(payload)
  const masterUrn = operatorAssignedPlayerMasterPlaylistUrn(
    payload.assignedMasterPlaylistUrn ?? payload.playerGroup?.masterPlaylistUrn ?? null
  )
  const activeCampaign = combineRegistrationScheduleRootsForPlayback(campaigns, getLocalAsUtcInstant(), {
    masterPlaylistUrn: masterUrn
  })
  const campaignStatus = activeCampaign !== null
    ? adaptCampaignToStatus(activeCampaign)
    : null

  return {
    tenant: {
      id: tenant.id,
      name: tenant.name ?? '',
      quotaExcess: tenant.quotaExcess
    },
    distributionLists: payload.distributionLists ?? [],
    playerGroup: playerGroupDisplayFromRegistrationPayload(payload),
    campaign: campaignStatus,
    platform: null,
    version: null,
    poolingPeriodMs: PLAYER_POOLING_PERIOD_MS
  }
}

export interface TPlayerRegistrationStatusAdapter {
  tenant: { id: string, name: string, quotaExcess: boolean }
  distributionLists: Array<{ id: string, name: string, superseedsPlayerMaster?: boolean }>
  playerGroup: TRegistrationStatusPlayerGroupDisplay | null
  campaign: TScheduleRootStatus | null
  platform: string | null
  version: string | null
  poolingPeriodMs: number
}

/** Maps a channel campaign assignment to TScheduleRootStatus for management UI surfaces. */
function adaptCampaignToStatus(
  assignment: IScheduleRootAssignment
): TScheduleRootStatus {
  const playlists: TScheduleRootPlaylistAssignment[] = assignment.playlists.map(
    pl => adaptPlaylistAssignment(pl, assignment.scheduleRootUrn)
  )
  return {
    urn: assignment.scheduleRootUrn,
    scheduleRootUrn: assignment.scheduleRootUrn,
    title: assignment.title,
    description: assignment.description,
    aspectRatio: aspectRatioFromPlaylistFrameLayouts(assignment.playlists[0]?.frames ?? []),
    playbackMode: assignment.playbackMode,
    customSequence: assignment.customSequence,
    widgetConfig: assignment.widgetConfig ?? null,
    status: CampaignStatus.PUBLISHED,
    statusReason: null,
    distributionListAssignment: null,
    playerGroupAssignment: null,
    playlists,
    contentHash: null,
    version: 1,
    simpleFeedPlaylistUrn: null
  }
}

/** Maps IScheduleRootAssignment | null to TScheduleRootStatus | null for status displays. */
export function campaignAssignmentToStatus(
  assignment: IScheduleRootAssignment | null
): TScheduleRootStatus | null {
  if (assignment === null) {
    return null
  }
  return adaptCampaignToStatus(assignment)
}

/** Maps a channel playlist assignment to TScheduleRootPlaylistAssignment. */
function adaptPlaylistAssignment(
  pl: IChannelPayloadPlaylistAssignment,
  campaignUrn: string
): TScheduleRootPlaylistAssignment {
  const toDate = (iso: string | null): Date | null =>
    iso !== null ? new Date(Temporal.Instant.from(iso.length === 10 && !iso.includes('T') ? `${iso}T00:00:00.000Z` : iso).epochMilliseconds) : null
  return {
    id: pl.id,
    campaignUrn,
    playlistUrn: pl.playlistUrn,
    index: pl.index,
    startDate: toDate(pl.startDate),
    endDate: toDate(pl.endDate),
    timeBlocks: pl.timeBlocks,
    playWithoutInterruption: pl.playWithoutInterruption,
    publishedVersionId: null,
    playlist: {
      title: pl.title,
      frames: pl.frames,
      metadata: {},
      widgetConfig: pl.widgetConfig ?? undefined
    }
  }
}
